import {SubmissionChannel, UpdateSubmissionChannelDto} from "@/models/submission-channel.ts";
import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import log from '@/lib/logger';

const NBH_SUBMISSION_CHANNELS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/submission-channels`
const NBH_SUBMISSION_CHANNELS_ADMIN_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/submission-channels`

export async function fetchAllSubmissionChannel(token: string | null): Promise<SubmissionChannel[] | void> {
    if (!token) {
        log.error('No token provided. Can not fetch submission channels.');
        return Promise.reject("No token provided.")
    }

    const response = await fetch(`${NBH_SUBMISSION_CHANNELS_BASE_URL}`, {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })

    return handleResponse<SubmissionChannel[]>(response)

}

export async function createSubmissionChannel(
    token: string | null,
    entry: SubmissionChannel
): Promise<SubmissionChannel | void> {
    if (!token) {
        log.error('No token provided. Can not create submission channel.');
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_SUBMISSION_CHANNELS_ADMIN_BASE_URL}`, {
        method: "POST",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(entry)
    })

    return handleResponse<SubmissionChannel>(response)
}

export async function setDefaultSubmissionChannel(
    token: string | null,
    submissionChannelId: string
): Promise<void> {
    if (!token) {
        log.error('No token provided. Can not set new default submission channel.');
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_SUBMISSION_CHANNELS_ADMIN_BASE_URL}/${submissionChannelId}/default`, {
        method: "PATCH",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })

    return handleResponse<void>(response)
}

export async function deleteSubmissionChannel(token: string | null, submissionChannelId: string): Promise<void> {
    if (!token) {
        log.error('No token provided. Can not delete submission channel.');
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_SUBMISSION_CHANNELS_ADMIN_BASE_URL}/${submissionChannelId}`, {
        method: "DELETE",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })

    return handleResponse<void>(response)
}

export async function updateSubmissionChannelOrder(token: string | null, sortedIds: string[]): Promise<void | SubmissionChannel[]> {
    if (!token) {
        log.error("No token provided. Can not update submission channel order.")
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_SUBMISSION_CHANNELS_ADMIN_BASE_URL}/sort-order`, {
        method: "PATCH",
        headers: new Headers({
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }),
        body: JSON.stringify({ sortedIds })
    });

    return handleResponse<SubmissionChannel[]>(response);
}

export async function updateSubmissionChannelDetails(
    token: string | null,
    dto: UpdateSubmissionChannelDto
): Promise<void | SubmissionChannel> {
    if (!token) {
        log.error("No token provided. Can not update submission channel.")
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const {id, ...payload} = dto

    const response = await fetch(`${NBH_SUBMISSION_CHANNELS_ADMIN_BASE_URL}/${id}`, {
        method: "PATCH",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    })

    return handleResponse<SubmissionChannel>(response)
}
